/**
 * LMS Access Control Service
 * Handles enrollment validation, device binding, session management
 * Production-ready implementation for student course access
 */

import axios from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getApiBaseUrl } from './httpClient';
import { hasUnlimitedCourseAccess } from '../utils/studentDevAccess';

const API_BASE_URL = getApiBaseUrl();

// Device fingerprint singleton
let fpPromise = null;
const getDeviceFingerprint = async () => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  const fp = await fpPromise;
  const result = await fp.get();
  return result.visitorId;
};

/**
 * Check if student has access to a specific course
 * @param {string} courseId - Course identifier
 * @param {object} studentData - Student information with enrolled_courses
 * @returns {object} { hasAccess, reason, details }
 */
export const checkCourseAccess = async (courseId, studentData) => {
  try {
    const skipExpiryCheck = hasUnlimitedCourseAccess(studentData);

    // 1. Check enrollment
    const enrolledCourses = studentData.enrolled_courses || [];
    const isEnrolled = enrolledCourses.some(
      course => String(course).toUpperCase() === String(courseId).toUpperCase()
    );

    if (!isEnrolled) {
      return {
        hasAccess: false,
        reason: 'not_enrolled',
        message: 'You are not enrolled in this course',
        details: { courseId, enrolled: enrolledCourses }
      };
    }

    // 2. Check student status
    if (studentData.status === 'inactive' || studentData.status === 'deleted') {
      return {
        hasAccess: false,
        reason: 'inactive_account',
        message: 'Your account is inactive. Please contact admin',
        details: { status: studentData.status }
      };
    }

    // 3. Check per-course expiry (student_course_access) or legacy joining_date fallback
    const courseAccessList = studentData.course_access || [];
    const normalizedCourseId = String(courseId).trim().toLowerCase();
    const courseAccess = courseAccessList.find(
      (item) => String(item.course_code || '').trim().toLowerCase() === normalizedCourseId,
    );

    let expiryDate;
    if (skipExpiryCheck) {
      expiryDate = null;
    } else if (courseAccess?.expires_at) {
      expiryDate = new Date(courseAccess.expires_at);
    } else if (studentData.joining_date) {
      const joiningDate = new Date(studentData.joining_date);
      const durationDays = studentData.duration_days || 45;
      expiryDate = new Date(joiningDate);
      expiryDate.setDate(expiryDate.getDate() + durationDays);
    }

    const now = new Date();
    const isExpired = !skipExpiryCheck && (
      courseAccess?.is_expired === true || (expiryDate && now > expiryDate)
    );

    if (isExpired) {
      return {
        hasAccess: false,
        reason: 'expired',
        message: expiryDate
          ? `Your course access expired on ${expiryDate.toLocaleDateString()}`
          : 'Your course access has expired',
        details: {
          expiryDate,
          courseAccess,
          joiningDate: studentData.joining_date,
          durationDays: studentData.duration_days,
        },
      };
    }

    // 4. Fetch course details to check if published
    const courseResponse = await axios.get(`${API_BASE_URL}/lms/courses/${courseId}/builder`);
    const courseData = courseResponse.data || {};

    if (!courseData.success) {
      return {
        hasAccess: false,
        reason: 'course_not_found',
        message: 'Course not found',
        details: { courseId }
      };
    }

    // Treat missing status as published when published content exists
    const hasPublishedContent = (courseData.chapters || []).some(
      chapter => chapter.status === 'published' || (chapter.items || []).some(item => item.status === 'published')
    );
    const courseStatus = courseData.status || courseData.course?.status || (hasPublishedContent ? 'published' : 'draft');

    if (courseStatus !== 'published' && !hasPublishedContent) {
      return {
        hasAccess: false,
        reason: 'course_unpublished',
        message: 'This course is not yet available',
        details: { status: courseStatus, hasPublishedContent }
      };
    }

    // 5. Check device binding (if enabled for course)
    const deviceCheckResult = await checkDeviceBinding(
      studentData.phone,
      courseId,
      courseData.deliveryMode || 'web'
    );

    if (!deviceCheckResult.allowed) {
      return {
        hasAccess: false,
        reason: 'device_mismatch',
        message: deviceCheckResult.message,
        details: deviceCheckResult.details
      };
    }

    // All checks passed
    return {
      hasAccess: true,
      reason: 'success',
      message: 'Access granted',
      details: {
        courseId,
        expiryDate,
        daysRemaining: expiryDate
          ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
          : null,
        deviceBinding: deviceCheckResult.details
      }
    };

  } catch (error) {
    console.error('Error checking course access:', error);
    return {
      hasAccess: false,
      reason: 'error',
      message: error.response?.data?.message || 'Failed to verify access',
      details: { error: error.message }
    };
  }
};

/**
 * Check device binding for student
 * @param {string} studentPhone - Student phone number
 * @param {string} courseId - Course ID
 * @param {string} mode - 'web' or 'mobile'
 * @returns {object} { allowed, message, details }
 */
export const checkDeviceBinding = async (studentPhone, courseId, mode = 'web') => {
  try {
    const deviceId = await getDeviceFingerprint();
    const platform = mode === 'mobile' ? 'mobile' : 'web';

    // Check with backend if device is registered
    const response = await axios.post(`${API_BASE_URL}/lms/check-device`, {
      studentPhone,
      courseId,
      deviceId,
      platform
    });

    if (response.data.allowed) {
      return {
        allowed: true,
        message: 'Device verified',
        details: {
          deviceId,
          platform,
          registeredAt: response.data.registeredAt
        }
      };
    } else {
      return {
        allowed: false,
        message: response.data.message || 'Device not registered',
        details: {
          deviceId,
          platform,
          registeredDevice: response.data.registeredDevice,
          canReset: response.data.canReset,
          lastResetDate: response.data.lastResetDate
        }
      };
    }
  } catch (error) {
    // If device binding endpoint doesn't exist or fails, allow access (graceful degradation)
    console.warn('Device binding check failed, allowing access:', error.message);
    return {
      allowed: true,
      message: 'Device binding not enforced',
      details: { error: error.message }
    };
  }
};

/**
 * Register current device for student
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @param {string} mode 
 * @returns {object}
 */
export const registerDevice = async (studentPhone, courseId, mode = 'web') => {
  try {
    const deviceId = await getDeviceFingerprint();
    const platform = mode === 'mobile' ? 'mobile' : 'web';

    const response = await axios.post(`${API_BASE_URL}/lms/register-device`, {
      studentPhone,
      courseId,
      deviceId,
      platform,
      userAgent: navigator.userAgent
    });

    return {
      success: true,
      message: 'Device registered successfully',
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to register device',
      error: error.message
    };
  }
};

/**
 * Request device reset (allowed once per month)
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @returns {object}
 */
export const requestDeviceReset = async (studentPhone, courseId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/lms/reset-device`, {
      studentPhone,
      courseId
    });

    return {
      success: true,
      message: 'Device reset successful',
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to reset device',
      error: error.message
    };
  }
};

/**
 * Get enrolled courses for student with access status
 * @param {object} studentData 
 * @returns {Array} Courses with access status
 */
export const getEnrolledCoursesWithAccess = async (studentData) => {
  const enrolledCourses = studentData.enrolled_courses || [];
  const coursesWithStatus = [];

  for (const courseCode of enrolledCourses) {
    try {
      const accessCheck = await checkCourseAccess(courseCode, studentData);
      coursesWithStatus.push({
        courseCode,
        ...accessCheck
      });
    } catch (error) {
      coursesWithStatus.push({
        courseCode,
        hasAccess: false,
        reason: 'error',
        message: 'Failed to check access',
        error: error.message
      });
    }
  }

  return coursesWithStatus;
};

export default {
  checkCourseAccess,
  checkDeviceBinding,
  registerDevice,
  requestDeviceReset,
  getEnrolledCoursesWithAccess,
  getDeviceFingerprint
};
